/**
 * Movement — the first real rules the engine carries, and the template for
 * every rule after it.
 *
 * Three things are being established here beyond "the `@` moves":
 *
 * - **The client never moves itself.** It predicts (M0-05) and is corrected.
 *   Every legality question — is that tile terrain you can stand on, is
 *   somebody already standing there, has your cooldown elapsed — is answered
 *   here, on the server's copy of the world, from world state alone.
 * - **Timing is integer arithmetic.** See `MOVE_COST` below. A float in a
 *   cooldown is a replay that diverges on the fourteen-thousandth tick.
 * - **Joining and leaving are intents too.** They could have been direct
 *   mutations the room performs on world state, and that would have been
 *   simpler by about ten lines — at the cost of the property the whole
 *   milestone exists for. If a player appearing is an intent, then a recorded
 *   session replays into the identical world; if it is a poke from the
 *   transport layer, the recording is missing the most important events in it.
 */
import { chebyshev, createActor, DIRECTION_VECTORS, isDiagonal, } from './actor.js';
import { deriveCapabilities, lostLeg } from './body.js';
import { parseJoin, parseLeave, parseMove, } from './actions.js';
import { burdenSpeed, sneakSpeed, burdenStaminaCost, createCombatant, isCommitted, isHelpless, kitOf, } from './fighter.js';
import { addItem, createInventory, mobilityAid } from './item.js';
import { isPassable, moveCostAt } from './map.js';
import { createMind } from './mind.js';
import { isResting } from './needs.js';
import { bleedOf, BLOOD_THRESHOLD, emissionFor, groundHold, isFleeing, layStep, massOf, } from './sign.js';
/**
 * Movement cooldowns are counted in thousandths of a tick.
 *
 * The design asks for diagonals to cost √2 (M0-04), and √2 is not an integer
 * number of ticks — but world state has to hash identically on every machine,
 * so no float may be stored. Milliticks resolve it: a diagonal costs 2,828
 * of them against a cardinal step's 2,000, all state stays integral, and the
 * error against a true √2 is one part in ten thousand.
 *
 * The fractional remainder is *carried* rather than rounded away, which is
 * what makes the average come out right. Walking a long diagonal alternates
 * naturally between three-tick and two-tick steps and arrives when 2.828
 * ticks per tile says it should — rounding each step to 3 would make the
 * diagonal 6% slower than specified, and every pathfinder built on top of it
 * would inherit the lie.
 */
export const MILLITICKS_PER_TICK = 1000;
/** Walk speed: two ticks per tile (design spec §9.2 / M0-04 default). */
export const TICKS_PER_STEP = 2;
export const MOVE_COST = {
    cardinal: TICKS_PER_STEP * MILLITICKS_PER_TICK,
    /** `round(2000 × √2)`. Computed, not typed, so the constant cannot drift. */
    diagonal: Math.round(TICKS_PER_STEP * MILLITICKS_PER_TICK * Math.SQRT2),
};
/**
 * How much unspent cooldown an idle actor may bank: just under one tick.
 *
 * Without a cap, standing still for a minute would buy a burst of free steps.
 * Without any carry at all, the fractional part of a diagonal would be
 * discarded every step and √2 would quietly become 3. This is the line
 * between the two.
 */
export const MOVE_CARRY_LIMIT = MILLITICKS_PER_TICK - 1;
/**
 * Slowest a crippled but still-mobile character walks, as a fraction of speed.
 *
 * The floor matters more than the ceiling: without one, a mobility approaching
 * zero produces a step cost approaching infinity, and "can barely walk" becomes
 * "is a statue" through arithmetic rather than through a decision anybody made.
 * Below this the character is not walking at all — they are prone, and getting
 * up is the action they owe.
 */
export const MIN_SPEED = 0.25;
/**
 * How fast a character with a leg gone gets about with nothing to lean on
 * (M4-05): a crawl, and the same fraction as the floor above — which is the
 * honest reading of both. Below this is not movement, it is lying down.
 *
 * The reason this is a *cap* rather than a capability penalty: `mobility` is a
 * weighted sum over legs and feet, and a man with one whole leg still sums to
 * half of one. Half speed is a limp. A missing leg is not a limp, and the
 * difference between the two is the entire aftermath of an amputation — you
 * crawl until somebody carves you a crutch (`mobilityAid`), and then you walk
 * badly for the rest of your life.
 */
export const CRAWL_SPEED = 0.25;
/**
 * How fast a body still moves (M2-03).
 *
 * This is the mechanical half of the hamstring: a level-2 cut to the thigh
 * impairs the leg, `deriveCapabilities` turns that into less `mobility`, and
 * here it becomes a longer cooldown between steps. Without this the wound
 * tables could take a stag's leg off and it would still outrun the hunter,
 * which is the single most important thing a placed blow is *for*.
 *
 * Returns 1 for anything with no body, so a cart moves at the speed a cart
 * always moved at.
 */
export function speedOf(plan, combatant, aid = 0) {
    if (!plan || !combatant)
        return 1;
    const { mobility } = deriveCapabilities(plan, combatant.vitals.body);
    const speed = Math.max(MIN_SPEED, mobility);
    // A leg that is gone caps what is left, and a crutch raises the cap (M4-05).
    // `aid` is what the satchel is worth (`mobilityAid`), passed in rather than
    // read here, because movement may not know what an inventory is — and because
    // a caller with no items table at all should still get the crawl right.
    if (!lostLeg(plan, combatant.vitals.body))
        return speed;
    return Math.min(speed, Math.max(CRAWL_SPEED, aid));
}
/**
 * What one step costs this body, in milliticks. Integer, as everything is.
 *
 * `terrainCost` is the ground being stepped *onto* (M3-01) — the bog you are
 * entering, not the bank you are leaving. Charging for the destination is what
 * makes the cost readable: the tile you can see under your cursor is the tile
 * whose price you are about to pay, and a player deciding whether to cross a
 * ford is deciding about the ford.
 *
 * Rounded, like the diagonal, because milliticks are the only place a fraction
 * is allowed to live — and rounded *after* the division so a slow body in a bog
 * pays the product rather than the product of two roundings.
 */
export function stepCost(direction, speed, terrainCost = 1) {
    const base = isDiagonal(direction) ? MOVE_COST.diagonal : MOVE_COST.cardinal;
    return Math.round((base * Math.max(0, terrainCost)) / Math.max(MIN_SPEED, speed));
}
/** Milliticks elapsed at the start of `tick`. */
function now(tick) {
    return tick * MILLITICKS_PER_TICK;
}
/**
 * Has this actor's movement cooldown elapsed by `tick`?
 *
 * Takes the cooldown rather than a whole `Actor` so the client's predictor can
 * ask the same question of its predicted position (M0-05), which has no id and
 * no name. Prediction agreeing with the server *by construction* is worth more
 * than a tidier signature.
 */
export function canMoveAt(actor, tick) {
    return now(tick) >= actor.moveReadyAt;
}
/** Ticks until this actor may move again — 0 if it may move now. */
export function ticksUntilMove(actor, tick) {
    return Math.max(0, Math.ceil((actor.moveReadyAt - now(tick)) / MILLITICKS_PER_TICK));
}
/**
 * Whoever is standing on a tile, if anyone.
 *
 * A linear scan of the actor table. At MVP scale (≤8 players in one commote,
 * design spec §9.2) that is a handful of comparisons per move intent, and a
 * position index in world state would be a second source of truth that can
 * disagree with the first. When M5's raids put a hundred actors in the valley,
 * this is the function to put an index behind — and the only one.
 */
export function actorAt(world, x, y, exceptId) {
    for (const actor of Object.values(world.actors)) {
        if (actor.x === x && actor.y === y && actor.id !== exceptId)
            return actor;
    }
    return undefined;
}
/** Terrain allows it *and* nobody is there. The full standing test. */
export function canStand(world, x, y, exceptId) {
    return isPassable(world.map, x, y) && actorAt(world, x, y, exceptId) === undefined;
}
/** Actors within `radius` steps (Chebyshev) of a point. */
export function actorsWithin(world, x, y, radius) {
    return Object.values(world.actors)
        .filter((actor) => chebyshev(actor.x, actor.y, x, y) <= radius)
        .sort((left, right) => (left.id < right.id ? -1 : left.id > right.id ? 1 : 0));
}
/**
 * A tile to drop a new actor on: near the arrival point, spreading outward
 * only as far as it has to.
 *
 * Arrivals must land **near each other**, which is the whole reason worldgen
 * clears a patch at the middle of the map. Sampling the map uniformly instead
 * — which this did at first — scatters players across sixteen thousand tiles,
 * so two people opening two browsers start a hundred tiles apart with no way
 * to find one another. That is M0's exit criterion failing quietly, and it
 * looked fine in every single-player test.
 *
 * The search widens in rings so a crowded clearing still seats everyone, and
 * the exhaustive scan at the end is the guarantee that a nearly-full map does
 * too. Every step draws from the world's `spawn` stream, so the same seed and
 * the same join order always produce the same starting positions.
 */
export function findSpawn(world, rng, near) {
    const { map } = world;
    if (map.width === 0 || map.height === 0)
        return null;
    // The world's own spawn point (M3-02: the village green) before the middle of
    // the map. Preferred here rather than passed in by every caller because it is
    // a fact about the world, and a caller that forgot it would seat somebody in
    // whatever the centre of the valley happens to be — which, given a river runs
    // down the middle of it, is often the river.
    const origin = near ??
        world.spawn ?? { x: Math.floor(map.width / 2), y: Math.floor(map.height / 2) };
    const dice = rng('spawn');
    const reach = Math.max(map.width, map.height);
    // Widen the search only when the closer ground is taken, so a warband that
    // arrives together stays together.
    for (let radius = 4; radius < reach * 2; radius *= 2) {
        for (let attempt = 0; attempt < 32; attempt += 1) {
            const x = origin.x + dice.int(-radius, radius + 1);
            const y = origin.y + dice.int(-radius, radius + 1);
            if (canStand(world, x, y))
                return { x, y };
        }
    }
    for (let y = 0; y < map.height; y += 1) {
        for (let x = 0; x < map.width; x += 1) {
            if (canStand(world, x, y))
                return { x, y };
        }
    }
    return null;
}
/**
 * Give an arriving actor a body, if this world has content to build one from
 * (M2-01).
 *
 * Built at the join rather than lazily on the first swing, because a body is
 * the thing that gets *hurt*: a bystander who only grew vitals when they
 * attacked could stand in a wolf pack and be fine.
 */
function seatBody(ctx, actorId, loadout, vitals) {
    const chosen = loadout ?? ctx.catalogue.defaultLoadout;
    if (!chosen)
        return;
    const plan = ctx.catalogue.plan(chosen.planId);
    if (!plan)
        return;
    // A returning character brings their own body (M3-07). Checked against the
    // plan they are being seated under rather than trusted: a saved body whose
    // parts do not match the plan it is arriving on would give the fight rules a
    // limb table with holes in it, and the day that happens is the day somebody
    // changed a body plan between releases. A fresh body is the safe answer, and
    // losing a character's wounds to it is better than losing the tick.
    const carried = vitals && bodyFits(vitals, plan) ? vitals : undefined;
    ctx.world.combat[actorId] = createCombatant({
        loadout: chosen,
        plan,
        ...(carried ? { vitals: carried } : {}),
    });
}
/** Whether a saved body has exactly the parts the plan it is arriving on names. */
function bodyFits(vitals, plan) {
    if (vitals.body.planId !== plan.id)
        return false;
    return plan.parts.every((part) => vitals.body.parts[part.id] !== undefined);
}
/**
 * Give an arriving creature something to decide with (M2-05).
 *
 * Keyed off the body plan, so "what a wolf is" and "how a wolf behaves" are one
 * decision made in content rather than two that can disagree. Players never get
 * one — there is no `human` instinct, and an actor whose instinct the catalogue
 * cannot supply simply stands there rather than taking the tick down.
 */
function seatMind(ctx, actor, payload) {
    if (actor.kind !== 'creature')
        return;
    const planId = (payload.loadout ?? ctx.catalogue.defaultLoadout)?.planId;
    if (!planId)
        return;
    const instinct = ctx.catalogue.instinct?.(planId);
    if (!instinct)
        return;
    ctx.world.minds[actor.id] = createMind({
        instinctId: instinct.id,
        x: actor.x,
        y: actor.y,
        packId: payload.packId ?? null,
    });
}
/* ------------------------------------------------------------------------- */
export const joinHandler = {
    type: 'join',
    parse: parseJoin,
    check(intent, ctx) {
        if (ctx.world.actors[intent.actorId]) {
            return `${intent.actorId} is already in the world`;
        }
        const at = intent.payload.at;
        if (at && !canStand(ctx.world, at.x, at.y)) {
            return `nothing can stand at (${at.x}, ${at.y})`;
        }
        if (!at && findSpawn(ctx.world, ctx.rng) === null) {
            return 'the world has nowhere to stand';
        }
        return null;
    },
    apply(intent, ctx) {
        // `check` has already proved one of these two branches resolves. Note that
        // re-running `findSpawn` here draws from the spawn stream a second time,
        // which is fine and deterministic — the stream exists to be drawn from,
        // and both calls happen on every replay in the same order.
        const at = intent.payload.at ?? findSpawn(ctx.world, ctx.rng);
        if (!at)
            return;
        const actor = createActor({
            id: intent.actorId,
            kind: intent.payload.kind ?? 'player',
            name: intent.payload.name,
            x: at.x,
            y: at.y,
        });
        ctx.world.actors[intent.actorId] = actor;
        seatBody(ctx, actor.id, intent.payload.loadout, intent.payload.vitals);
        seatMind(ctx, actor, intent.payload);
        if (intent.payload.carried && !ctx.world.inventories[actor.id]) {
            const inventory = createInventory();
            for (const carried of intent.payload.carried) {
                const item = ctx.catalogue.item?.(carried.itemId);
                if (!item)
                    continue;
                addItem(inventory, item.id, carried.count, intent.tick, item.stackable);
            }
            ctx.world.inventories[actor.id] = inventory;
        }
        // Arrivals and departures are announced by the rule that causes them, not
        // reconstructed by the server from a before/after diff of the actor table.
        // A diff cannot tell a walk-in from a resurrection, and it is a second
        // place that has to learn about every future way of entering the world.
        ctx.emit({
            type: 'joined',
            actors: [actor.id],
            refs: [actor.name],
            text: `${actor.name} arrives.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    },
};
export const leaveHandler = {
    type: 'leave',
    parse: parseLeave,
    check(intent, ctx) {
        if (!ctx.world.actors[intent.actorId])
            return `${intent.actorId} is not in the world`;
        return null;
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return;
        delete ctx.world.actors[intent.actorId];
        // The body goes off-stage with the actor. A combat entry with nobody
        // standing on it would be a wound clock ticking over an empty tile.
        delete ctx.world.combat[intent.actorId];
        delete ctx.world.minds[intent.actorId];
        ctx.emit({
            type: 'left',
            actors: [actor.id],
            refs: [actor.name],
            text: `${actor.name} is gone.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    },
};
export const moveHandler = {
    type: 'move',
    parse: parseMove,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return `${intent.actorId} is not in the world`;
        // Down is not dead, but it is not walking either (M2-07). Checked here
        // rather than left to the client, because "the server decides" is the
        // property the whole intent pipeline exists for — and before this, a
        // corpse could be steered around the valley.
        const combatant = ctx.world.combat[intent.actorId];
        if (combatant && isHelpless(ctx.catalogue, combatant, ctx.tick)) {
            return `${actor.name} cannot move`;
        }
        // A fight is entered and left deliberately.  A break-off keeps both people
        // exposed for its one-exchange cost; once it resolves, ordinary movement is
        // free again.
        const heldByAnother = Object.entries(ctx.world.combat).some(([id, other]) => id !== actor.id && other.engagedWith === actor.id);
        if (combatant?.engagedWith !== null && combatant?.engagedWith !== undefined) {
            return combatant.withdrawingUntil > ctx.tick
                ? `${actor.name} is still breaking off`
                : `${actor.name} must break off before moving`;
        }
        if (heldByAnother)
            return `${actor.name} must break off before moving`;
        if (combatant !== undefined && isCommitted(combatant, ctx.tick)) {
            return `${actor.name} is committed to an exchange`;
        }
        if (!canMoveAt(actor, ctx.tick)) {
            return `still moving: ${ticksUntilMove(actor, ctx.tick)} tick(s) to go`;
        }
        const [dx, dy] = DIRECTION_VECTORS[intent.payload.direction];
        const x = actor.x + dx;
        const y = actor.y + dy;
        if (!isPassable(ctx.world.map, x, y))
            return `(${x}, ${y}) is not passable`;
        // Whoever you are hauling is not in your way (M4-06). Without this, a
        // dragged body lying in the direction you happen to be facing blocks the
        // step that would have pulled it out of the way — a rescuer standing
        // deadlocked against the man he is trying to save.
        const held = combatant?.burden?.targetId ?? null;
        const there = actorAt(ctx.world, x, y, actor.id);
        if (there && there.id !== held)
            return `(${x}, ${y}) is occupied`;
        return null;
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return;
        // Guard is more than a passive defence bonus: a creature already within
        // reach cannot slip past someone who is deliberately holding that ground.
        // This starts an ordinary exchange, rather than a free hit, so the player
        // still chooses the blow and the normal engagement rules keep the animal
        // in reach. It is especially important for rabbits: their speed is an
        // escape advantage until a hunter finally corners one.
        const guarder = guardingPlayerAt(ctx, actor);
        if (guarder) {
            const guard = ctx.world.combat[guarder.id];
            const creature = ctx.world.combat[actor.id];
            if (guard && creature) {
                const mind = ctx.world.minds[actor.id];
                if (mind) {
                    // Being held at bay is the one situation in which a prey animal
                    // cannot simply bolt. It gets a real exchange in which the hunter
                    // can strike; a wound will put it back into `flee`.
                    mind.state = 'fight';
                    mind.since = ctx.tick;
                    mind.targetId = guarder.id;
                    mind.seenAt = ctx.tick;
                    mind.seenX = guarder.x;
                    mind.seenY = guarder.y;
                    mind.guardedBy = guarder.id;
                }
                guard.engagedWith = actor.id;
                creature.engagedWith = guarder.id;
                ctx.emit({
                    type: 'status',
                    actors: [guarder.id, actor.id],
                    refs: ['guard', 'intercepted'],
                    text: `${guarder.name} holds ${actor.name} at bay. Press A to strike.`,
                    at: { x: guarder.x, y: guarder.y },
                });
                return;
            }
        }
        const direction = intent.payload.direction;
        const [dx, dy] = DIRECTION_VECTORS[direction];
        actor.x += dx;
        actor.y += dy;
        actor.facing = direction;
        // Rest is tied to the tile a player chose, not an instruction that survives
        // a reconnect. The durable rest timer is deliberately cleared on logout,
        // while the durable body can still arrive prone, so standing up cannot be
        // conditional on `isResting`: every successful voluntary step puts a
        // movable body back on its feet. `campSystem` retains the equivalent check
        // for movement imposed on somebody else.
        const needs = ctx.world.needs[actor.id];
        const combatant = ctx.world.combat[actor.id];
        if (needs && isResting(needs))
            needs.restingSince = 0;
        if (combatant?.vitals.prone)
            combatant.vitals.prone = false;
        // A hurt leg costs speed (M2-03). Read through the catalogue rather than
        // stored on the actor, because it is derived from the wounds and a cached
        // copy is a cached copy somebody forgets to invalidate the first time a
        // splint comes off.
        const plan = combatant ? ctx.catalogue.plan(combatant.loadout.planId) : null;
        // …and a body over your shoulder costs more again (M4-06). Multiplied into
        // the speed rather than added to the cost, so that hauling a man uphill
        // through a bog is as slow as it should be: the two penalties compound,
        // which is what makes the direct route worth arguing about.
        const cost = stepCost(direction, speedOf(plan, combatant, mobilityAid(ctx.world.inventories[actor.id], (id) => ctx.catalogue.item?.(id) ?? null)) *
            burdenSpeed(combatant) *
            sneakSpeed(combatant), moveCostAt(ctx.world.map, actor.x, actor.y));
        // The wind it takes. Paid per step rather than per second, so standing
        // still holding somebody is free and walking with them is not — which is
        // both true and the thing that makes a rescue a race.
        if (combatant?.burden) {
            const load = ctx.world.combat[combatant.burden.targetId]?.loadout.attributes.frame ?? null;
            combatant.staminaMilli = Math.max(0, combatant.staminaMilli - burdenStaminaCost(combatant, load));
        }
        // Carry the unspent remainder forward, but never more than `MOVE_CARRY_LIMIT`.
        const from = Math.max(actor.moveReadyAt, now(ctx.tick) - MOVE_CARRY_LIMIT);
        actor.moveReadyAt = from + cost;
        // What the step left on the ground (M3-08).
        //
        // Here rather than in a system that watches for movement, and that is the
        // whole placement argument: this is the one place in the game where
        // something moves, it already knows the direction, and every future way of
        // moving an actor will come through it. A watcher would have to
        // reconstruct the heading it is being handed, and would miss anything that
        // ever moved somebody without walking them.
        layTracksFor(ctx, actor, direction);
        // Archived, not narrated. Forty rows a second of "Gwion steps north" is
        // exactly the telemetry M0-07 wants queryable by tick and actor, and
        // exactly what would drown a log pane if anyone were shown it.
        ctx.emit({
            type: 'moved',
            actors: [actor.id],
            refs: [direction, String(actor.x), String(actor.y)],
            audience: 'archive',
            at: { x: actor.x, y: actor.y },
        });
    },
};
/** A guarded player with a melee weapon in reach of this creature, if any. */
function guardingPlayerAt(ctx, creature) {
    if (creature.kind !== 'creature')
        return null;
    for (const actor of Object.values(ctx.world.actors)) {
        if (actor.kind !== 'player')
            continue;
        const guard = ctx.world.combat[actor.id];
        if (!guard?.guarding ||
            guard.engagedWith !== null ||
            guard.withdrawingUntil > ctx.tick ||
            isCommitted(guard, ctx.tick)) {
            continue;
        }
        const kit = kitOf(ctx.catalogue, guard);
        if (!kit || kit.weapon.ranged)
            continue;
        if (chebyshev(actor.x, actor.y, creature.x, creature.y) > kit.timing.reach)
            continue;
        // A guard may catch the initial bolt, but it may not turn an animal that
        // has already broken off into an immortal pinball. It is still fleeing this
        // same person until it loses the trail and settles.
        const mind = ctx.world.minds[creature.id];
        if (mind?.state === 'flee' && mind.guardedBy === actor.id)
            continue;
        return actor;
    }
    return null;
}
/**
 * Mark the tile this actor has just stepped onto (M3-08).
 *
 * The species is the body plan, so an animal leaves its own kind of print and a
 * player leaves a human one — players are tracked exactly as animals are, which
 * is deliberate: trailing a wounded rival back to their camp is a thing this
 * game should permit.
 *
 * Its own RNG stream per actor, like the infection rolls, so one creature
 * crossing a bog cannot shift the dice of another crossing a moor.
 */
export function layTracksFor(ctx, actor, direction) {
    const hold = groundHold(ctx.world.map, actor.x, actor.y);
    const combatant = ctx.world.combat[actor.id];
    const bleedRate = bleedOf(combatant);
    // Nothing to leave and nothing to leave it on: the common case on rock and
    // water, and worth returning before the stream is even drawn from.
    if (hold <= 0 && bleedRate < BLOOD_THRESHOLD)
        return;
    const emissions = emissionFor({
        hold,
        fleeing: isFleeing(combatant, ctx.world.minds[actor.id]?.state),
        bleedRate,
        roll: ctx.rng(`sign:${actor.id}`).next(),
        // How heavy the thing was (M3-10): a rabbit's print is faint and a stag's is
        // plain, off the frame its body was seated with rather than a new field.
        mass: massOf(combatant),
    });
    if (emissions.length === 0)
        return;
    const speciesId = combatant?.loadout.planId ?? actor.kind;
    layStep(ctx.world.signs, actor, speciesId, direction, ctx.tick, emissions);
}
/**
 * Everything M0-04 teaches the world to do. `say` and `examine` are in
 * `speech.ts` (M0-06); combat arrives in M2-01.
 */
export const MOVEMENT_INTENTS = [
    joinHandler,
    leaveHandler,
    moveHandler,
];
//# sourceMappingURL=movement.js.map